import { motion } from "motion/react";

// Background images
const galaxyImg = "https://images.unsplash.com/photo-1711147047707-59b09d3e8ff1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZ2FsYXh5JTIwY29zbW9zJTIwbmVidWxhJTIwYmxhY2t8ZW58MXx8fHwxNzczMTYxNjc4fDA&ixlib=rb-4.1.0&q=80&w=1080";

const eyeImg = "https://images.unsplash.com/photo-1759396283043-1e38582b6b4b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhbGwlMjBzZWVpbmclMjBleWUlMjBvY2N1bHQlMjBkYXJrfGVufDF8fHx8MTc3MzE2MTY3OXww&ixlib=rb-4.1.0&q=80&w=1080";

const ritualImg = "https://images.unsplash.com/photo-1603669388505-28d290e9b053?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwb2NjdWx0JTIwcml0dWFsJTIwY2FuZGxlcyUyMGdvdGhpY3xlbnwxfHx8fDE3NzMxNjE2Nzl8MA&ixlib=rb-4.1.0&q=80&w=1080";

export const MP = {
  blood: "#6b0015",
  crimson: "#8b0020",
  crimsonBright: "#a81030",
  crimsonGlow: "rgba(107,0,21,0.75)",
  crimsonPale: "rgba(168,16,48,0.55)",
  gold: "#c87814",
  goldMid: "#e88c1e",
  goldBright: "#ffa028",
  goldPale: "#ffc846",
  goldGlow: "rgba(200,120,20,0.7)",
  stone: "#1e1a28",
  stoneMid: "#2a2232",
  stoneDark: "#120e1a",
  stoneGlow: "rgba(30,26,40,0.8)",
  abyss: "#06040c",
  abyssWarm: "#0a0508",
  parchment: "rgba(255,255,255,0.90)",
  parchmentDim: "rgba(255,255,255,0.52)",
  oxford: "#1e1a28",
  oxfordMid: "#2a2232",
  oxfordDim: "rgba(30,26,40,0.7)",
  oxford2: "#1e1a28",
  wine: "#6b0015",
  wineBright: "#a81030",
  winePale: "#c83050",
  wineGlow: "rgba(107,0,21,0.75)",
  wineDeep: "#2a0008",
  pearl: "rgba(255,255,255,0.90)",
  pearlDim: "rgba(255,255,255,0.52)",
  black: "#06040c",
  deepNavy: "#06040c",
  deepSpace: "#05030a",
  teal: "#8b0020",
  tealBright: "#a81030",
  tealDim: "#6b0015",
  tealGlow: "rgba(107,0,21,0.7)",
  violet: "#5a0a30",
  violetBright: "#7a1040",
  violetPale: "#c83050",
  violetGlow: "rgba(90,10,48,0.65)",
  blue: "#3a0010",
  blueGlow: "rgba(58,0,16,0.5)",
};

export function MysticalBg({ variant = "home" }) {
  const isSplash = variant === "splash";

  const bgSrc =
    isSplash
      ? galaxyImg
      : variant === "intel"
      ? ritualImg
      : eyeImg;

  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 0 }}
    >

      {/* 1 ── BASE */}
      <div
        className="absolute inset-0"
        style={{
          background: isSplash
            ? "linear-gradient(160deg, #05030a 0%, #08040e 45%, #060210 100%)"
            : "linear-gradient(160deg, #06040c 0%, #090508 45%, #070308 100%)",
        }}
      />

      {/* 2 ── Background image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            radial-gradient(
              circle at top,
              rgba(120,0,255,0.10) 0%,
              rgba(60,0,120,0.08) 24%,
              rgba(10,8,20,0.78) 60%,
              rgba(0,0,0,0.96) 100%
            ),
            url(${bgSrc})
          `,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          opacity: 0.62,
          mixBlendMode: "luminosity",
          filter: `
            saturate(0.42)
            brightness(0.58)
            contrast(1.12)
          `,
        }}
      />

      {/* 3 ── Darkness overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(6,4,12,0.55) 0%, rgba(4,2,8,0.25) 40%, rgba(6,4,12,0.62) 100%)",
        }}
      />

      {/* 4 ── Blood corners */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 55% 45% at 0% 0%, rgba(90,0,12,0.55) 0%, transparent 65%),
            radial-gradient(ellipse 50% 45% at 100% 100%, rgba(70,0,10,0.5) 0%, transparent 65%)
          `,
        }}
      />

      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0.4, 0.75, 0.4] }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 80%, rgba(80,0,15,0.5) 0%, transparent 60%)",
        }}
      />

      {/* 5 ── Gold center */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        style={{
          background:
            "radial-gradient(ellipse 40% 28% at 50% 45%, rgba(200,120,20,0.35) 0%, transparent 65%)",
        }}
      />

      {/* 6 ── Dark sides */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            linear-gradient(
              90deg,
              rgba(20,15,25,0.7) 0%,
              rgba(15,10,20,0.35) 18%,
              transparent 40%,
              transparent 60%,
              rgba(15,10,20,0.35) 82%,
              rgba(20,15,25,0.7) 100%
            )
          `,
        }}
      />

      {/* 7 ── Masonic grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(107,0,21,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(107,0,21,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }}
      />

      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(45deg, rgba(200,120,20,0.04) 1px, transparent 1px),
            linear-gradient(-45deg, rgba(30,26,40,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "85px 85px",
        }}
      />

      {/* 8 ── Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 100% 100% at 50% 50%, transparent 30%, rgba(4,2,8,0.5) 65%, rgba(2,1,4,0.88) 100%)",
          zIndex: 7,
        }}
      />

      {/* 9 ── Film grain */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")",
          backgroundRepeat: "repeat",
          backgroundSize: "200px 200px",
          opacity: 0.35,
          mixBlendMode: "overlay",
          zIndex: 8,
        }}
      />
    </div>
  );
}

